// =============================================================================
// 🔴 ETI-101 — ÉTAT ET DÉCISION D'UNE PORTE DE VALIDATION, DANS LA FICHE MÉTIER
// =============================================================================
// Un seul composant, posé dans les fiches EXISTANTES (prospect, dossier, devis) —
// ⛔ pas un écran autonome de gestion des portes.
//
// 🔑 CE QUE LE COMPOSANT FAIT : il AFFICHE l'état rendu par le serveur
//    (`portes.etat_porte`) et DÉCLENCHE les routes de décision existantes.
// ⛔ CE QU'IL NE FAIT PAS : il ⛔ ne décide ni quelle porte s'applique, ni si elle
//    est active, ni qui peut décider — tout cela vient de la réponse serveur,
//    pilotée par `tenants.config.portes`. Le serveur reste seul juge : un bouton
//    affiché ⛔ ne vaut pas autorisation (la garde est côté API).
// ⛔ Un tenant SANS porte configurée reçoit `active: false` ⇒ le composant ne
//    rend RIEN — aucun changement visuel ni fonctionnel.
// ⛔ Aucun nom de tenant ici (témoin : PorteDecision.temoin.test.js).
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { OhCard, OhStatusNote } from "../../ui/oh-charte";
import { ACTES, deciderPorte, lirePorte, surfaceConnue } from "./portesApi";

// Libellé et famille de couleur de chaque état RENDU par le serveur.
const ETATS = {
  en_attente_validation: { libelle: "En attente de validation", couleur: "amber" },
  validee: { libelle: "Validée", couleur: "green" },
  refusee: { libelle: "Refusée", couleur: "red" },
};
const ETAT_INCONNU = { libelle: "État inconnu", couleur: "orange" };

function utilisateurCourant() {
  try { return JSON.parse(localStorage.getItem("user") || "{}") || {}; } catch { return {}; }
}

/** Le rôle courant peut-il décider ? ⚠️ On LIT la liste `valideurs` rendue par
 *  le serveur — ⛔ aucune liste de rôles recopiée ici. */
export function peutDecider(etat, role, estSuperAdmin) {
  const valideurs = (etat && etat.valideurs) || [];
  if (role && valideurs.includes(role)) return true;
  return !!estSuperAdmin && valideurs.includes("super_admin");
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString("fr-FR");
}

export default function PorteDecision({
  entiteType, entiteId, tenantId, role, actionSuivante, onDecided, className = "",
}) {
  const [etat, setEtat] = useState(null);         // l'état serveur, ou null
  const [erreurConfig, setErreurConfig] = useState(null);
  const [saisie, setSaisie] = useState(null);     // null | "refuser" | "rouvrir"
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    if (!entiteId || !surfaceConnue(entiteType)) { setEtat(null); return; }
    try {
      const r = await lirePorte(entiteType, entiteId, tenantId);
      if (r.ok) {
        setErreurConfig(null);
        setEtat(((r.corps && r.corps.portes) || [])[0] || null);
      } else if (r.status === 409) {
        // 🔑 Configuration DÉCLARÉE mais invalide (ETI-69, fail-closed) : on le
        // DIT, ⛔ on ne se tait pas — sinon l'étape aval refuserait sans raison visible.
        setEtat(null);
        setErreurConfig((r.corps && r.corps.detail) || "Configuration des portes invalide.");
      } else {
        setEtat(null);           // 401/403/404 : rien à afficher
      }
    } catch {
      setEtat(null);
    }
  }, [entiteType, entiteId, tenantId]);

  useEffect(() => { charger(); }, [charger]);

  if (erreurConfig) {
    return (
      <OhStatusNote variant="error" title="Validation indisponible" className={className}
                    data-testid={`porte-${entiteType}-config-invalide`}>
        {erreurConfig}
      </OhStatusNote>
    );
  }
  if (!etat || etat.active === false || etat.etat === "non_applicable") return null;

  const user = utilisateurCourant();
  const roleEffectif = role || user.role || user["rôle"] || null;
  const decideur = peutDecider(etat, roleEffectif, user.is_super_admin === true);
  const vue = ETATS[etat.etat] || ETAT_INCONNU;
  const testid = `porte-${entiteType}`;

  const decider = async (acte) => {
    if ((acte === ACTES.refuser || acte === ACTES.rouvrir) && !motif.trim()) return;
    setEnvoi(true);
    try {
      const r = await deciderPorte(entiteType, entiteId, etat.porte, acte,
                                   acte === ACTES.valider ? "" : motif, tenantId);
      if (!r.ok) {
        // ⛔ Aucun refus muet : le motif du serveur s'affiche tel quel.
        const d = r.corps && r.corps.detail;
        toast.error(typeof d === "string" ? d : `Décision refusée (HTTP ${r.status})`);
        return;
      }
      toast.success({ valider: `Porte ${etat.porte} validée`,
                      refuser: `Porte ${etat.porte} refusée`,
                      rouvrir: `Porte ${etat.porte} rouverte` }[acte]);
      setSaisie(null);
      setMotif("");
      await charger();
      if (onDecided) onDecided(r.corps);
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <OhCard className={className} data-testid={testid} data-porte={etat.porte} data-etat={etat.etat}>
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} aria-hidden="true" />
        <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
          Validation {etat.porte}
        </span>
        <span className="text-xs px-2 py-0.5"
              style={{
                background: `var(--oh-stat-${vue.couleur}-bg)`,
                color: `var(--oh-stat-${vue.couleur}-text)`,
                border: `1px solid var(--oh-stat-${vue.couleur}-border)`,
                borderRadius: "var(--oh-radius-control)",
              }}
              data-testid={`${testid}-etat`}>
          {vue.libelle}
        </span>
      </div>

      {(etat.auteur || etat.horodatage || etat.motif) && (
        <p className="text-xs mt-2" style={{ color: "var(--oh-text-secondary)", lineHeight: 1.6 }}
           data-testid={`${testid}-trace`}>
          {etat.auteur && <>Par {etat.auteur}</>}
          {etat.horodatage && <>{etat.auteur ? " · " : ""}le {formatDate(etat.horodatage)}</>}
          {etat.motif && <><br />Motif : <span data-testid={`${testid}-motif`}>{etat.motif}</span></>}
        </p>
      )}

      {!decideur && etat.etat === "en_attente_validation" && (
        <p className="text-xs mt-2" style={{ color: "var(--oh-text-muted)" }}
           data-testid={`${testid}-lecture-seule`}>
          Décision réservée aux rôles : {(etat.valideurs || []).join(", ") || "—"}.
        </p>
      )}

      {decideur && saisie === null && (
        <div className="flex items-center gap-2 flex-wrap mt-3">
          {etat.etat === "en_attente_validation" && (
            <>
              <Button size="sm" disabled={envoi} onClick={() => decider(ACTES.valider)}
                      style={{ background: "var(--oh-accent-solid)" }}
                      data-testid={`${testid}-valider`}>
                <Check className="w-4 h-4 mr-1" aria-hidden="true" /> Valider
              </Button>
              <Button size="sm" variant="outline" disabled={envoi}
                      onClick={() => { setSaisie(ACTES.refuser); setMotif(""); }}
                      style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)" }}
                      data-testid={`${testid}-refuser`}>
                <X className="w-4 h-4 mr-1" aria-hidden="true" /> Refuser
              </Button>
            </>
          )}
          {etat.etat === "refusee" && (
            <Button size="sm" variant="outline" disabled={envoi}
                    onClick={() => { setSaisie(ACTES.rouvrir); setMotif(""); }}
                    style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)" }}
                    data-testid={`${testid}-rouvrir`}>
              <RotateCcw className="w-4 h-4 mr-1" aria-hidden="true" /> Rouvrir
            </Button>
          )}
        </div>
      )}

      {decideur && saisie !== null && (
        <div className="mt-3 space-y-2">
          <label htmlFor={`${testid}-motif-saisie`} className="text-xs font-medium"
                 style={{ color: "var(--oh-text-secondary)" }}>
            {saisie === ACTES.refuser ? "Motif du refus (obligatoire)" : "Motif de la réouverture (obligatoire)"}
          </label>
          <Textarea id={`${testid}-motif-saisie`} value={motif} rows={2}
                    onChange={(ev) => setMotif(ev.target.value)}
                    placeholder="Expliquer la décision"
                    data-testid={`${testid}-motif-saisie`} />
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={envoi || !motif.trim()} onClick={() => decider(saisie)}
                    style={{ background: "var(--oh-accent-solid)" }}
                    data-testid={`${testid}-confirmer`}>
              {saisie === ACTES.refuser ? "Confirmer le refus" : "Confirmer la réouverture"}
            </Button>
            <Button size="sm" variant="ghost" disabled={envoi}
                    onClick={() => { setSaisie(null); setMotif(""); }}
                    style={{ color: "var(--oh-text-secondary)" }}
                    data-testid={`${testid}-annuler`}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* 🔑 L'ÉTAPE SUIVANTE : fournie par la fiche hôte (le geste EXISTANT), et
          proposée seulement quand le serveur dit « validée ». Le serveur reste juge. */}
      {etat.etat === "validee" && actionSuivante && (
        <div className="mt-3">
          <Button size="sm" variant="outline" onClick={actionSuivante.onClick}
                  style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)" }}
                  data-testid={`${testid}-suite`}>
            {actionSuivante.label}
          </Button>
        </div>
      )}
    </OhCard>
  );
}
